import { EncodingValidationRequest, EncodingValidationResponse } from 'stylewhere/api'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'

export type PreEncodingDuplicateRole = 'EMBEDDED_L' | 'EMBEDDED_R'

export type PreEncodingDuplicateRoleContext = {
  duplicateRole: PreEncodingDuplicateRole
  missingRole: PreEncodingDuplicateRole
  extraTagCode: string
}

const isDuplicateRole = (role?: string): role is PreEncodingDuplicateRole =>
  role === 'EMBEDDED_L' || role === 'EMBEDDED_R'

export const getPreEncodingDuplicateRoleContext = (
  operation: EncodingOperationConfig | undefined,
  response?: EncodingValidationResponse,
  encodingValidation?: EncodingValidationRequest
): PreEncodingDuplicateRoleContext | undefined => {
  // eslint-disable-next-line @typescript-eslint/dot-notation
  if (operation?.attributes['enableGucciDuplicateEmbeddedLRCorrection'] !== 'true' || !response?.errors?.length) {
    return undefined
  }

  const extraErrors = response.errors.filter(
    (error) => error.errorCode === 'EXTRA_TAG' && isDuplicateRole(error.payload?.role)
  )
  const missingErrors = response.errors.filter(
    (error) => error.errorCode === 'MISSING_MANDATORY_TAG' && isDuplicateRole(error.ref)
  )
  if (extraErrors.length !== 1 || missingErrors.length !== 1) return undefined

  const extraError = extraErrors[0]
  const missingError = missingErrors[0]
  if (!extraError.ref) return undefined

  const duplicateRole = extraError.payload?.role
  const missingRole = missingError.ref
  if (!isDuplicateRole(duplicateRole) || !isDuplicateRole(missingRole)) return undefined
  if (duplicateRole === missingRole) return undefined

  const readOnDuplicateSide =
    encodingValidation?.identifiers?.filter((identifier) => !!identifier.code && identifier.role === duplicateRole) ??
    []
  if (readOnDuplicateSide.length === 0) return undefined

  const extraInItem = response.item?.identifiers?.some((identifier) => identifier.code === extraError.ref)
  if (extraInItem) return undefined

  return {
    duplicateRole,
    missingRole,
    extraTagCode: extraError.ref
  }
}

/** Swaps side digits at EPC positions 11–12 between 01 and 02. */
export const swapPreEncodingSideDigits = (epc: string): string => {
  if (epc.length < 12) return epc
  const sideDigits = epc.slice(10, 12)
  if (sideDigits === '02') return `${epc.slice(0, 10)}01${epc.slice(12)}`
  if (sideDigits === '01') return `${epc.slice(0, 10)}02${epc.slice(12)}`
  return epc
}

/** Write password dedicated to the pre-encoding correction, if configured on the operation */
export const getPreEncodingCorrectionTagPassword = (operation: EncodingOperationConfig): string | undefined =>
  // eslint-disable-next-line @typescript-eslint/dot-notation
  operation.attributes['tagCorrectionWritePassword'] || undefined
